/**
 * Intelligence detection cores — shared, dependency-free.
 *
 * This file is the single source of truth for the pure deterministic detection
 * math behind the five intelligence agents:
 *
 *   Fracture (cassandra)  — decision red-team fragility detection
 *   Compass               — strategy-to-priority alignment detection
 *   Keystone              — dependency-graph leverage & cycle detection
 *   Meridian              — execution-drift (commitment vs attention) detection
 *   Condition Watch       — Condition B/C candidate detection (governance
 *                           watching itself; candidates only, never conditions)
 *
 * It is imported by BOTH runtimes:
 *   - the Vite client at src/lib/intelligence/* (browser TypeScript), and
 *   - the Supabase edge function at supabase/functions/ambient (Deno).
 *
 * Because it must load identically in the browser bundle and in Deno, it MUST
 * stay dependency-free: ZERO imports, no Deno or browser globals, no repo
 * imports. Detection here is exact maths — the LLM narration, data loading,
 * and persistence live with each runtime's orchestrator, never in this file.
 */

// ── Shared finding shapes ────────────────────────────────────────────────────────

export type CoreSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info';

/**
 * The narration-free part of a finding. Structurally assignable to
 * Omit<NewFinding, 'summary' | 'detail'> from src/lib/agentFindingsService.ts.
 */
export interface CoreFindingBase {
  findingType: string;
  severity: CoreSeverity;
  title: string;
  itemIds: string[];
  data: Record<string, unknown>;
}

export interface CoreDraftFinding {
  base: CoreFindingBase;
  persona?: string;
  findingKind: string;
  fallbackSummary: string;
  fallbackDetail: string;
  /** Used to order/cap findings before narration. */
  weight: number;
}

/** Cap applied by every agent's runner after sorting drafts by weight. */
export const MAX_FINDINGS = 15;

// ═════════════════════════════════════════════════════════════════════════════════
// Fracture (cassandra) — Decision Red-Team detection
//
// Fracture stress-tests a ranking that *looks* settled. It reads the raw
// per-criterion, per-participant scores a session already produced (group_scores
// + group_criteria weights) and computes — deterministically — three kinds of
// fragility the published list never shows:
//
//   knife_edge      Two adjacent priorities separated by a razor-thin margin,
//                   where a small shift in one criterion's weight flips them.
//   lone_criterion  A top priority that only ranks high because of ONE criterion;
//                   remove it and the item collapses down the list.
//   hidden_dissent  A top priority with a buried minority that scored it near the
//                   floor — strong opposition the mean silently averaged away.
// ═════════════════════════════════════════════════════════════════════════════════

const KNIFE_EDGE_GAP = 0.18;      // weighted-score gap (1-5 scale) considered "knife edge"
const FLIP_WEIGHT_MAX = 0.15;     // <=15% weight shift to flip ⇒ fragile
const LONE_RANK_DROP = 3;         // positions an item falls when its top criterion is removed
const DISSENT_FLOOR = 2.0;        // a dissenter's avg score at/below this on a high-mean item
const DISSENT_MEAN = 3.5;         // item must be broadly liked for dissent to be "hidden"

// ── Internal shapes ─────────────────────────────────────────────────────────────

export interface Criterion { id: string; title: string; weight: number; }
export interface ScoreRow { sessionId: string; itemId: string; criteriaId: string; userId: string; score: number; }

export interface SessionData {
  sessionId: string;
  criteria: Criterion[];
  scores: ScoreRow[];
  itemTitles: Map<string, string>;
}

export const CASSANDRA_PERSONA = 'Fracture, a decision red-team analyst who exposes the hidden fragility in a prioritization that everyone assumes is settled';

// ── Maths helpers ────────────────────────────────────────────────────────────────

function mean(xs: number[]): number {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;
}

/** Mean score for (item, criterion) across all raters. */
function cellMean(scores: ScoreRow[], itemId: string, criteriaId: string): number {
  const xs = scores.filter((s) => s.itemId === itemId && s.criteriaId === criteriaId).map((s) => s.score);
  return mean(xs);
}

/** Weighted overall score for an item across the given criteria set. */
function weightedScore(scores: ScoreRow[], itemId: string, criteria: Criterion[]): number {
  const totalW = criteria.reduce((a, c) => a + c.weight, 0);
  if (totalW === 0) return 0;
  const sum = criteria.reduce((a, c) => a + c.weight * cellMean(scores, itemId, c.id), 0);
  return sum / totalW;
}

function rankItems(itemIds: string[], scores: ScoreRow[], criteria: Criterion[]): string[] {
  return [...itemIds].sort((a, b) => weightedScore(scores, b, criteria) - weightedScore(scores, a, criteria));
}

// ── Detection ────────────────────────────────────────────────────────────────────

export function detectCassandraFindings(session: SessionData): CoreDraftFinding[] {
  const { criteria, scores, itemTitles } = session;
  const itemIds = [...new Set(scores.map((s) => s.itemId))];
  const findings: CoreDraftFinding[] = [];

  if (itemIds.length < 2 || criteria.length < 1) return findings;

  const ranked = rankItems(itemIds, scores, criteria);
  const _totalW = criteria.reduce((a, c) => a + c.weight, 0);
  const title = (id: string) => itemTitles.get(id) ?? `Item ${id.slice(0, 8)}`;
  const _rankOf = (id: string) => ranked.indexOf(id) + 1;

  // ── Knife-edge: adjacent pairs with a tiny gap that one weight shift can flip ──
  for (let r = 0; r < ranked.length - 1; r++) {
    const hi = ranked[r], lo = ranked[r + 1];
    const sHi = weightedScore(scores, hi, criteria);
    const sLo = weightedScore(scores, lo, criteria);
    const gap = sHi - sLo;
    if (gap > KNIFE_EDGE_GAP) continue;

    // Criterion where the lower item most outperforms the higher one.
    let bestC: Criterion | null = null;
    let bestDelta = 0; // how much lo beats hi on this criterion
    for (const c of criteria) {
      const d = cellMean(scores, lo, c.id) - cellMean(scores, hi, c.id);
      if (d > bestDelta) { bestDelta = d; bestC = c; }
    }
    if (!bestC || bestDelta <= 0) continue;

    // Fraction of total weight that, moved onto bestC, closes the gap.
    const flipFraction = gap / (bestDelta + gap);
    if (flipFraction > FLIP_WEIGHT_MAX) continue;

    const severity: CoreSeverity = r === 0 ? 'critical' : r < 3 ? 'high' : 'medium';
    findings.push({
      base: {
        findingType: 'knife_edge',
        severity,
        title: `#${r + 1} and #${r + 2} are a coin-flip apart`,
        itemIds: [hi, lo],
        data: {
          higher: { id: hi, title: title(hi), rank: r + 1, score: round(sHi) },
          lower:  { id: lo, title: title(lo), rank: r + 2, score: round(sLo) },
          gap: round(gap),
          pivotCriterion: bestC.title,
          weightShiftToFlipPct: Math.round(flipFraction * 100),
          sessionId: session.sessionId,
        },
      },
      persona: CASSANDRA_PERSONA,
      findingKind: 'knife-edge ranking pair',
      fallbackSummary: `Ranks #${r + 1} (“${title(hi)}”) and #${r + 2} (“${title(lo)}”) are only ${round(gap)} points apart — a ${Math.round(flipFraction * 100)}% weight shift toward “${bestC.title}” flips them.`,
      fallbackDetail: `The order of these two priorities is not robust: it hinges on the current weight of “${bestC.title}”, on which #${r + 2} actually scores higher. Before publishing, confirm that weight is deliberate — or run a tiebreak, because this pair could swap on any reasonable re-weighting.`,
      weight: (10 - r) + (FLIP_WEIGHT_MAX - flipFraction) * 10,
    });
  }

  // ── Lone-criterion capture: top-third item propped up by a single criterion ──
  const topThird = Math.max(1, Math.ceil(ranked.length / 3));
  for (let r = 0; r < topThird; r++) {
    const id = ranked[r];
    if (criteria.length < 2) break;

    // Contribution share of each criterion to this item's weighted score.
    let topC: Criterion | null = null;
    let topShare = 0;
    const denom = criteria.reduce((a, c) => a + c.weight * cellMean(scores, id, c.id), 0);
    if (denom <= 0) continue;
    for (const c of criteria) {
      const share = (c.weight * cellMean(scores, id, c.id)) / denom;
      if (share > topShare) { topShare = share; topC = c; }
    }
    if (!topC) continue;

    // Re-rank with the dominant criterion removed.
    const without = criteria.filter((c) => c.id !== topC!.id);
    const newRank = rankItems(itemIds, scores, without).indexOf(id) + 1;
    const drop = newRank - (r + 1);

    if (topShare >= 0.5 && drop >= LONE_RANK_DROP) {
      const severity: CoreSeverity = r === 0 ? 'critical' : r < 3 ? 'high' : 'medium';
      findings.push({
        base: {
          findingType: 'lone_criterion',
          severity,
          title: `#${r + 1} rests on one criterion`,
          itemIds: [id],
          data: {
            item: { id, title: title(id), rank: r + 1 },
            dominantCriterion: topC.title,
            contributionPct: Math.round(topShare * 100),
            rankWithoutCriterion: newRank,
            rankDrop: drop,
            sessionId: session.sessionId,
          },
        },
        persona: CASSANDRA_PERSONA,
        findingKind: 'single-criterion concentration',
        fallbackSummary: `“${title(id)}” is ranked #${r + 1}, but ${Math.round(topShare * 100)}% of that comes from one criterion (“${topC.title}”) — drop it and the item falls to #${newRank}.`,
        fallbackDetail: `This priority looks broadly strong but isn't: its position is almost entirely carried by “${topC.title}”. If that criterion is contested or over-weighted, the item has no second leg to stand on. Pressure-test whether “${title(id)}” truly deserves the top of the list, or whether one dimension is masking weakness elsewhere.`,
        weight: (8 - r) + topShare * 4 + Math.min(drop, 6) * 0.5,
      });
    }
  }

  // ── Hidden dissent: a broadly-liked top item with a buried minority of detractors ──
  const halfway = Math.max(1, Math.ceil(ranked.length / 2));
  for (let r = 0; r < halfway; r++) {
    const id = ranked[r];
    // Per-user average score for this item.
    const byUser = new Map<string, number[]>();
    for (const s of scores) {
      if (s.itemId !== id) continue;
      const arr = byUser.get(s.userId) ?? [];
      arr.push(s.score);
      byUser.set(s.userId, arr);
    }
    if (byUser.size < 3) continue;

    const userAvgs = [...byUser.entries()].map(([userId, xs]) => ({ userId, avg: mean(xs) }));
    const itemMean = mean(userAvgs.map((u) => u.avg));
    if (itemMean < DISSENT_MEAN) continue; // only "hidden" if the item is generally liked

    const dissenters = userAvgs.filter((u) => u.avg <= DISSENT_FLOOR);
    if (dissenters.length < 2) continue;

    const severity: CoreSeverity = r === 0 ? 'critical' : r < 3 ? 'high' : 'medium';
    findings.push({
      base: {
        findingType: 'hidden_dissent',
        severity,
        title: `#${r + 1} has buried opposition`,
        itemIds: [id],
        data: {
          item: { id, title: title(id), rank: r + 1 },
          meanScore: round(itemMean),
          dissenterCount: dissenters.length,
          raterCount: byUser.size,
          lowestDissenterAvg: round(Math.min(...dissenters.map((d) => d.avg))),
          sessionId: session.sessionId,
        },
      },
      persona: CASSANDRA_PERSONA,
      findingKind: 'hidden minority dissent',
      fallbackSummary: `“${title(id)}” ranks #${r + 1} with a healthy mean of ${round(itemMean)}, yet ${dissenters.length} of ${byUser.size} participants scored it near the floor.`,
      fallbackDetail: `Averaging hid a real split: a minority of ${dissenters.length} feels strongly that “${title(id)}” does not belong this high. That kind of suppressed disagreement is what resurfaces as a late veto after the list is published. Surface these participants' reasoning and resolve it now, while it's cheap.`,
      weight: (9 - r) + dissenters.length * 0.8,
    });
  }

  return findings;
}

function round(n: number): number { return Math.round(n * 100) / 100; }

// ═════════════════════════════════════════════════════════════════════════════════
// Compass — Strategy-to-Priority Alignment detection
//
// Compass holds the priority list up against what the organisation actually said
// its strategy is. It reads strategic_objectives and the objective↔priority
// mappings and surfaces — deterministically — where stated strategy and real
// priorities have quietly diverged:
//
//   orphaned_objective   A critical/high objective with NOTHING mapped to it.
//                        You said it matters; the backlog disagrees.
//   under_owned          A critical/high objective that has only weak (indirect)
//                        links — nominally covered, concretely owned by no one.
//   unbacked_priorities  Active items serving no objective at all — effort with no
//                        strategic rationale.
//   portfolio_imbalance  A low-priority objective hoarding primary effort while a
//                        critical one starves.
//
// The mappings (alignment_strength ∈ primary | secondary | indirect) are exact.
// ═════════════════════════════════════════════════════════════════════════════════

const UNBACKED_MIN = 3;   // only flag the unbacked-items aggregate above this many
const UNBACKED_SAMPLE = 8;

export const COMPASS_PERSONA = 'Compass, a strategy alignment analyst who catches the gap between what leadership says matters and what the priority list actually serves';

export type ObjPriority = 'critical' | 'high' | 'medium' | 'low';
export type Strength = 'primary' | 'secondary' | 'indirect';

export interface Objective { id: string; name: string; priority: ObjPriority; timeHorizon: string; status: string; }
export interface Mapping { objectiveId: string; priorityId: string; strength: Strength; }

const PRIORITY_RANK: Record<ObjPriority, number> = { critical: 0, high: 1, medium: 2, low: 3 };

// ── Detection ────────────────────────────────────────────────────────────────────

export function detectCompassFindings(
  objectives: Objective[],
  mappings: Mapping[],
  itemTitles: Map<string, string>,
  activeItemIds: string[],
): CoreDraftFinding[] {
  const findings: CoreDraftFinding[] = [];
  if (objectives.length === 0) return findings;

  const mapsByObj = new Map<string, Mapping[]>();
  for (const m of mappings) {
    const arr = mapsByObj.get(m.objectiveId) ?? [];
    arr.push(m);
    mapsByObj.set(m.objectiveId, arr);
  }

  // ── Orphaned & under-owned objectives ──
  for (const obj of objectives) {
    if (obj.status !== 'active') continue;
    if (obj.priority !== 'critical' && obj.priority !== 'high') continue;

    const maps = mapsByObj.get(obj.id) ?? [];
    const primaries = maps.filter((m) => m.strength === 'primary');

    if (maps.length === 0) {
      const severity: CoreSeverity = obj.priority === 'critical' ? 'critical' : 'high';
      findings.push({
        base: {
          findingType: 'orphaned_objective',
          severity,
          title: `“${obj.name}” has nothing driving it`,
          itemIds: [],
          data: { objective: obj.name, priority: obj.priority, timeHorizon: obj.timeHorizon, mappedItems: 0 },
        },
        findingKind: 'orphaned strategic objective',
        fallbackSummary: `Your ${obj.priority} objective “${obj.name}” has zero priorities mapped to it — the strategy says it matters, the backlog has nothing.`,
        fallbackDetail: `Nothing currently in the workspace is working toward “${obj.name}”, yet it's marked ${obj.priority}. Either it is no longer truly ${obj.priority} and should be re-leveled, or work to advance it must be created and prioritised. This is the kind of gap that surfaces as “why did we make no progress on our #1 goal?” at the next review.`,
        weight: 20 - PRIORITY_RANK[obj.priority] * 3,
      });
    } else if (primaries.length === 0) {
      const severity: CoreSeverity = obj.priority === 'critical' ? 'high' : 'medium';
      findings.push({
        base: {
          findingType: 'under_owned',
          severity,
          title: `“${obj.name}” is only loosely covered`,
          itemIds: maps.map((m) => m.priorityId).filter((id) => itemTitles.has(id)),
          data: {
            objective: obj.name, priority: obj.priority,
            mappedItems: maps.length,
            byStrength: {
              primary: 0,
              secondary: maps.filter((m) => m.strength === 'secondary').length,
              indirect: maps.filter((m) => m.strength === 'indirect').length,
            },
          },
        },
        findingKind: 'under-owned objective (no primary driver)',
        fallbackSummary: `“${obj.name}” (${obj.priority}) is touched by ${maps.length} item${maps.length === 1 ? '' : 's'}, but none of them owns it primarily.`,
        fallbackDetail: `A ${obj.priority} objective with only secondary/indirect links has no clear owner driving it forward — everyone is contributing to it on the side, no one is accountable for it. Designate a primary priority for “${obj.name}” so progress is somebody's explicit job.`,
        weight: 14 - PRIORITY_RANK[obj.priority] * 3,
      });
    }
  }

  // ── Unbacked priorities (active items serving no objective) ──
  const mappedItemIds = new Set(mappings.map((m) => m.priorityId));
  const unbacked = activeItemIds.filter((id) => !mappedItemIds.has(id));
  if (unbacked.length >= UNBACKED_MIN) {
    const sample = unbacked.slice(0, UNBACKED_SAMPLE);
    findings.push({
      base: {
        findingType: 'unbacked_priorities',
        severity: 'medium',
        title: `${unbacked.length} active items serve no objective`,
        itemIds: sample,
        data: {
          count: unbacked.length,
          sample: sample.map((id) => itemTitles.get(id) ?? `Item ${id.slice(0, 8)}`),
          objectivesDefined: objectives.length,
        },
      },
      findingKind: 'priorities with no strategic backing',
      fallbackSummary: `${unbacked.length} active items aren't mapped to any strategic objective — effort with no stated reason to exist.`,
      fallbackDetail: `These items will consume capacity that could advance your objectives. Either connect each to the objective it serves, or challenge whether it belongs in the cycle at all. Unbacked work is how a roadmap silently drifts away from strategy.`,
      weight: 6 + Math.min(unbacked.length, 20) * 0.2,
    });
  }

  // ── Portfolio imbalance: a low objective hoarding primaries vs a starved critical ──
  const primaryCount = new Map<string, number>();
  for (const m of mappings) {
    if (m.strength !== 'primary') continue;
    primaryCount.set(m.objectiveId, (primaryCount.get(m.objectiveId) ?? 0) + 1);
  }
  const criticalStarved = objectives.filter(
    (o) => o.status === 'active' && o.priority === 'critical' && (primaryCount.get(o.id) ?? 0) <= 1,
  );
  const lowHeavy = objectives
    .filter((o) => o.status === 'active' && (o.priority === 'low' || o.priority === 'medium'))
    .map((o) => ({ o, n: primaryCount.get(o.id) ?? 0 }))
    .filter((x) => x.n >= 3)
    .sort((a, b) => b.n - a.n)[0];

  if (criticalStarved.length > 0 && lowHeavy) {
    findings.push({
      base: {
        findingType: 'portfolio_imbalance',
        severity: 'high',
        title: 'Effort is pointed at the wrong objective',
        itemIds: [],
        data: {
          starvedCritical: criticalStarved.map((o) => o.name),
          overInvestedObjective: lowHeavy.o.name,
          overInvestedPriority: lowHeavy.o.priority,
          overInvestedPrimaries: lowHeavy.n,
        },
      },
      findingKind: 'portfolio imbalance vs stated priority',
      fallbackSummary: `“${lowHeavy.o.name}” (${lowHeavy.o.priority}) has ${lowHeavy.n} priorities driving it, while critical objective${criticalStarved.length > 1 ? 's' : ''} ${criticalStarved.map((o) => `“${o.name}”`).join(', ')} ${criticalStarved.length > 1 ? 'have' : 'has'} at most one.`,
      fallbackDetail: `Your concentration of effort is inverted relative to your stated priorities: a lower-priority objective is the best-resourced, and a critical one is nearly unstaffed. Rebalance primary ownership toward the critical objective, or explicitly re-level your strategy so it matches where the work is actually going.`,
      weight: 16,
    });
  }

  return findings;
}

// ═════════════════════════════════════════════════════════════════════════════════
// Keystone — Leverage Finder detection
//
// Keystone reads the dependency graph the workspace has already drawn between its
// items (item_relationship_edges, relationship_type = 'dependency') and finds the
// thing no ranked list ever shows: the unglamorous item that silently unblocks a
// large number of others.
//
//   keystone           A single item that, directly or transitively, blocks many
//                      downstream priorities. Sequence it first and everything
//                      behind it moves; ignore it and they all stall.
//   dependency_cycle   A circular chain of blockers — an impossible sequence that
//                      can never start, hiding in the graph.
//
// A directed dependency edge a → b means "a depends on b" (b is the prerequisite).
// Leverage = the count of distinct items that transitively depend on a node.
// ═════════════════════════════════════════════════════════════════════════════════

const KEYSTONE_MIN_REACH = 3;   // transitive dependents to qualify as a keystone

export const KEYSTONE_PERSONA = 'Keystone, a leverage analyst who finds the obscure, low-profile item that secretly unblocks a whole stack of priorities';

export interface RelEdge { source: string; target: string; directed: boolean; }
export interface ItemMeta { title: string; altitude: number | null; }

// ── Graph helpers ────────────────────────────────────────────────────────────────

/**
 * Build the "depends-on" adjacency: dependsOn[a] = items a needs first.
 * Undirected dependency edges are treated as mutual.
 */
function buildDependsOn(edges: RelEdge[]): Map<string, Set<string>> {
  const m = new Map<string, Set<string>>();
  const add = (a: string, b: string) => {
    const s = m.get(a) ?? new Set<string>();
    s.add(b);
    m.set(a, s);
  };
  for (const e of edges) {
    add(e.source, e.target);
    if (!e.directed) add(e.target, e.source);
  }
  return m;
}

/** reverse[b] = items that directly depend on b. */
function reverse(dependsOn: Map<string, Set<string>>): Map<string, Set<string>> {
  const rev = new Map<string, Set<string>>();
  for (const [a, targets] of dependsOn) {
    for (const b of targets) {
      const s = rev.get(b) ?? new Set<string>();
      s.add(a);
      rev.set(b, s);
    }
  }
  return rev;
}

/** All distinct nodes that transitively depend on `start` (excluding start). */
function transitiveDependents(start: string, rev: Map<string, Set<string>>): Set<string> {
  const seen = new Set<string>();
  const stack = [...(rev.get(start) ?? [])];
  while (stack.length) {
    const n = stack.pop()!;
    if (n === start || seen.has(n)) continue;
    seen.add(n);
    for (const up of rev.get(n) ?? []) {
      if (!seen.has(up)) stack.push(up);
    }
  }
  return seen;
}

/** Find one cycle in the directed depends-on graph, if any. */
function findCycle(directedDependsOn: Map<string, Set<string>>): string[] | null {
  const WHITE = 0, GRAY = 1, BLACK = 2;
  const color = new Map<string, number>();
  const parent = new Map<string, string | null>();

  const nodes = new Set<string>();
  for (const [a, ts] of directedDependsOn) { nodes.add(a); for (const t of ts) nodes.add(t); }

  let cycle: string[] | null = null;

  function dfs(u: string): boolean {
    color.set(u, GRAY);
    for (const v of directedDependsOn.get(u) ?? []) {
      if ((color.get(v) ?? WHITE) === GRAY) {
        // Reconstruct cycle v → ... → u → v
        const path = [v];
        let cur: string | null = u;
        while (cur && cur !== v) { path.push(cur); cur = parent.get(cur) ?? null; }
        path.push(v);
        cycle = path.reverse();
        return true;
      }
      if ((color.get(v) ?? WHITE) === WHITE) {
        parent.set(v, u);
        if (dfs(v)) return true;
      }
    }
    color.set(u, BLACK);
    return false;
  }

  for (const n of nodes) {
    if ((color.get(n) ?? WHITE) === WHITE) {
      parent.set(n, null);
      if (dfs(n)) break;
    }
  }
  return cycle;
}

// ── Detection ────────────────────────────────────────────────────────────────────

export function detectKeystoneFindings(edges: RelEdge[], meta: Map<string, ItemMeta>): CoreDraftFinding[] {
  const findings: CoreDraftFinding[] = [];
  if (edges.length === 0) return findings;

  const dependsOn = buildDependsOn(edges);
  const rev = reverse(dependsOn);
  const title = (id: string) => meta.get(id)?.title ?? `Item ${id.slice(0, 8)}`;

  const candidates = new Set<string>(rev.keys());
  for (const id of candidates) {
    const dependents = transitiveDependents(id, rev);
    if (dependents.size < KEYSTONE_MIN_REACH) continue;

    const directCount = (rev.get(id) ?? new Set()).size;
    const alt = meta.get(id)?.altitude ?? null;
    const higherAltitudeDependents = [...dependents].filter((d) => {
      const da = meta.get(d)?.altitude;
      return alt != null && da != null && da > alt;
    });

    // A low-altitude keystone blocking higher-altitude work is the sharpest case.
    const lowProfile = alt != null && alt <= 2;
    const reach = dependents.size;
    const severity: CoreSeverity =
      reach >= 8 || (lowProfile && higherAltitudeDependents.length >= 3) ? 'critical'
      : reach >= 5 ? 'high'
      : 'medium';

    const sample = [...dependents].slice(0, 8).map((d) => title(d));

    findings.push({
      base: {
        findingType: 'keystone',
        severity,
        title: `“${title(id)}” unblocks ${reach} item${reach === 1 ? '' : 's'}`,
        itemIds: [id, ...[...dependents].slice(0, 12)],
        data: {
          keystone: { id, title: title(id), altitude: alt },
          transitiveReach: reach,
          directDependents: directCount,
          higherAltitudeDependents: higherAltitudeDependents.length,
          sampleDependents: sample,
        },
      },
      findingKind: 'high-leverage keystone dependency',
      fallbackSummary: `“${title(id)}” looks minor but ${reach} other priorit${reach === 1 ? 'y' : 'ies'} can't proceed until it's done.`,
      fallbackDetail: `This item sits upstream of ${reach} others in your dependency graph (${directCount} directly). Its rank doesn't reflect its leverage: delaying it stalls everything behind it, while sequencing it first releases the whole chain. Pull “${title(id)}” forward regardless of where it landed on the list.`,
      weight: reach * 2 + (lowProfile ? 5 : 0) + higherAltitudeDependents.length,
    });
  }

  // ── Dependency cycle (directed edges only) ──
  const directed = new Map<string, Set<string>>();
  for (const e of edges) {
    if (!e.directed) continue;
    const s = directed.get(e.source) ?? new Set<string>();
    s.add(e.target);
    directed.set(e.source, s);
  }
  const cycle = findCycle(directed);
  if (cycle && cycle.length >= 2) {
    const uniq = [...new Set(cycle)];
    findings.push({
      base: {
        findingType: 'dependency_cycle',
        severity: 'high',
        title: `Circular dependency across ${uniq.length} items`,
        itemIds: uniq,
        data: { cycle: uniq.map((id) => title(id)) },
      },
      findingKind: 'circular dependency chain',
      fallbackSummary: `${uniq.length} items form a dependency loop: ${uniq.map((id) => `“${title(id)}”`).join(' → ')} → back to the start.`,
      fallbackDetail: `This chain can never start — each item waits on the next, all the way around the loop. It will quietly block delivery until someone breaks it. Decide which dependency in the cycle is actually optional and remove that edge so the rest can be sequenced.`,
      weight: 12 + uniq.length,
    });
  }

  return findings;
}

/** Parse an item's altitude level ('L2', '2', 2, …) into a number, or null. */
export function parseAltitude(raw: unknown): number | null {
  if (raw == null) return null;
  const n = parseInt(String(raw).replace(/[^0-9]/g, ''), 10);
  return Number.isFinite(n) ? n : null;
}

// ═════════════════════════════════════════════════════════════════════════════════
// Meridian — Execution-Drift detection
//
// Meridian reconciles what the organization *committed to* (the ratified
// priority ranking — priority_proposals.ranked_items) with where its energy is
// *actually going* (30-day connector activity from change_events, joined to
// items via entity_external_ids). It computes a per-item attention share and
// speaks only when commitment and attention diverge:
//
//   starved_priority  A top-quartile commitment drawing near-zero linked
//                     activity while other items get plenty. You ratified it;
//                     the connected systems say nobody is working on it.
//   attention_sink    An unranked or bottom-half item absorbing an outsized
//                     share of linked activity — energy flowing somewhere the
//                     ranking never sanctioned.
//
// Divergence is a prompt for inquiry, not an accusation: work may live outside
// the connected tools, or the ranking may simply be stale. The fallback copy
// says so explicitly, and the LLM persona is instructed likewise.
// ═════════════════════════════════════════════════════════════════════════════════

/** Activity window the loaders use and the evidence reports. */
export const MERIDIAN_WINDOW = '30d';
/** Below this many connector events in the window, stay silent — too little signal. */
export const MERIDIAN_MIN_TOTAL_EVENTS = 20;
/** Attention-share math needs at least this many *linked* events to mean anything. */
export const MERIDIAN_MIN_LINKED_EVENTS = 10;
/** Fraction of the ranking considered "top-quartile" commitment. */
export const MERIDIAN_TOP_QUARTILE = 0.25;
/** A top-quartile item at or below this share of linked activity is starved. */
export const MERIDIAN_STARVED_SHARE = 0.05;
/** ...but only if some other item draws at least this share (others get plenty). */
export const MERIDIAN_PLENTY_SHARE = 0.15;
/** An unranked/bottom-half item above this share of linked activity is a sink. */
export const MERIDIAN_SINK_SHARE = 0.25;
/** A sink at or above this share escalates to high severity even when ranked. */
export const MERIDIAN_SINK_HIGH_SHARE = 0.4;

export const MERIDIAN_PERSONA =
  'Meridian, an execution-drift sentinel who compares what the organization committed to with where its energy is actually going';

/** One entry of the ratified ranking (rank 1 = highest commitment). */
export interface RankedCommitment { itemId: string; title: string; rank: number; }
/** Linked connector activity for one item over the window. */
export interface ActivityCount { itemId: string; title?: string; events30d: number; }

// ── Detection ────────────────────────────────────────────────────────────────────

export function detectMeridianFindings(
  commitments: RankedCommitment[],
  activity: ActivityCount[],
  totalEvents: number,
): CoreDraftFinding[] {
  const findings: CoreDraftFinding[] = [];

  // No ratified ranking ⇒ nothing was committed to ⇒ nothing can drift.
  if (commitments.length === 0) return findings;
  // Too little signal to accuse anyone.
  if (totalEvents < MERIDIAN_MIN_TOTAL_EVENTS) return findings;

  const linkedTotal = activity.reduce((a, x) => a + x.events30d, 0);
  if (linkedTotal < MERIDIAN_MIN_LINKED_EVENTS) return findings;

  const n = commitments.length;
  const rankOf = new Map<string, number>();
  const commitTitle = new Map<string, string>();
  for (const c of commitments) {
    rankOf.set(c.itemId, c.rank);
    commitTitle.set(c.itemId, c.title);
  }
  const eventsOf = new Map<string, number>();
  const activityTitle = new Map<string, string>();
  for (const a of activity) {
    eventsOf.set(a.itemId, (eventsOf.get(a.itemId) ?? 0) + a.events30d);
    if (a.title) activityTitle.set(a.itemId, a.title);
  }
  const title = (id: string) =>
    commitTitle.get(id) ?? activityTitle.get(id) ?? `Item ${id.slice(0, 8)}`;
  const share = (id: string) => (eventsOf.get(id) ?? 0) / linkedTotal;
  const pct = (x: number) => Math.round(x * 100);

  const topQuartileRank = Math.max(1, Math.ceil(n * MERIDIAN_TOP_QUARTILE));
  const topHalfRank = Math.ceil(n / 2);

  // ── Starved priority: top-quartile commitment with near-zero attention ──
  for (const c of commitments) {
    if (c.rank > topQuartileRank) continue;
    const s = share(c.itemId);
    if (s > MERIDIAN_STARVED_SHARE) continue;

    // "While others get plenty" — the busiest *other* item must draw a real share.
    let busiestId: string | null = null;
    let busiestShare = 0;
    for (const id of eventsOf.keys()) {
      if (id === c.itemId) continue;
      const os = share(id);
      if (os > busiestShare) { busiestShare = os; busiestId = id; }
    }
    if (!busiestId || busiestShare < MERIDIAN_PLENTY_SHARE) continue;

    const events = eventsOf.get(c.itemId) ?? 0;
    const severity: CoreSeverity = c.rank <= 3 ? 'high' : 'medium';
    // rank-importance × divergence: 1.0 for #1, scaled by how far attention diverges.
    const rankImportance = (n - c.rank + 1) / n;
    const divergence = busiestShare - s;
    findings.push({
      base: {
        findingType: 'starved_priority',
        severity,
        title: `#${c.rank} “${c.title}” is drawing almost no execution activity`,
        itemIds: [c.itemId],
        data: {
          item: { id: c.itemId, title: c.title, rank: c.rank },
          sharePct: pct(s),
          events30d: events,
          linkedEvents: linkedTotal,
          totalEvents,
          window: MERIDIAN_WINDOW,
          listSize: n,
          busiestOther: { title: title(busiestId), sharePct: pct(busiestShare) },
        },
      },
      persona: MERIDIAN_PERSONA,
      findingKind: 'starved top-quartile priority (commitment vs attention)',
      fallbackSummary: `You ratified “${c.title}” at #${c.rank}, yet it drew ${pct(s)}% of linked execution activity in the last 30 days (${events} of ${linkedTotal} events) while “${title(busiestId)}” absorbed ${pct(busiestShare)}%.`,
      fallbackDetail: `The ranking says “${c.title}” is a top commitment; the connected systems show almost none of the last 30 days' activity touched it. That divergence is a prompt for inquiry, not an accusation — the work may be happening outside the connected tools, the item may be blocked, or it may have been quietly deprioritized. Ask the owning team what “${c.title}” is waiting on before the gap between the decision and reality widens further.`,
      weight: 20 * rankImportance * (0.25 + divergence),
    });
  }

  // ── Attention sink: unranked or bottom-half item absorbing an outsized share ──
  for (const id of eventsOf.keys()) {
    const s = share(id);
    if (s <= MERIDIAN_SINK_SHARE) continue;
    const rank = rankOf.get(id);
    if (rank != null && rank <= topHalfRank) continue; // top-half items may hog attention

    const events = eventsOf.get(id) ?? 0;
    const unranked = rank == null;
    const severity: CoreSeverity =
      unranked || s >= MERIDIAN_SINK_HIGH_SHARE ? 'high' : 'medium';
    // rank-importance × divergence: an unranked sink is maximally out of place.
    const rankPenalty = unranked ? 1 : rank / n;
    findings.push({
      base: {
        findingType: 'attention_sink',
        severity,
        title: unranked
          ? `Unranked “${title(id)}” is absorbing ${pct(s)}% of activity`
          : `#${rank} “${title(id)}” is absorbing ${pct(s)}% of activity`,
        itemIds: [id],
        data: {
          item: { id, title: title(id), rank: rank ?? null },
          ranked: !unranked,
          sharePct: pct(s),
          events30d: events,
          linkedEvents: linkedTotal,
          totalEvents,
          window: MERIDIAN_WINDOW,
          listSize: n,
        },
      },
      persona: MERIDIAN_PERSONA,
      findingKind: 'attention sink (unsanctioned share of execution activity)',
      fallbackSummary: `“${title(id)}” is ${unranked ? 'not on the ratified list' : `ranked #${rank} of ${n}`}, yet ${pct(s)}% of linked execution activity in the last 30 days landed on it (${events} of ${linkedTotal} events).`,
      fallbackDetail: `The organization's energy is concentrating on something the ranking ${unranked ? 'never sanctioned' : 'placed near the bottom'}. Treat this as a prompt for inquiry, not an accusation: the activity may be legitimate run-the-business work the list intentionally excludes — or the ranking may no longer reflect what actually needs doing. Ask why effort is flowing to “${title(id)}”, and if the answer is convincing, re-rank deliberately rather than letting the list and reality diverge in silence.`,
      weight: 20 * s * rankPenalty,
    });
  }

  return findings.sort((a, b) => b.weight - a.weight).slice(0, MAX_FINDINGS);
}

// ═════════════════════════════════════════════════════════════════════════════════
// Condition Watch — Condition B/C candidate detection (AMB-9)
//
// Condition Watch is governance watching itself. It cross-references the
// signals the workspace has on record (material agent findings, high-impact
// business events, hostile stakeholder pulse trends) against the governance
// acts that changed — or ratified — the plan (rescorings, reprioritization
// triggers, catchball balls, list approvals), and surfaces the two failure
// conditions of the Signal Framework (docs/1-canon/23-signal-framework.md §2)
// as CANDIDATES:
//
//   condition_b_candidate  A material signal (critical/high) that has sat on
//                          the record past the policy window with NO
//                          governance act after it — "is there a signal on
//                          the instruments we have seen and not responded
//                          to?" Severity: high for critical signals, medium
//                          otherwise.
//   condition_c_candidate  A governance act with NO material signal in the
//                          window before it — "what changed, specifically,
//                          that is causing this change in priority?"
//                          Severity: medium.
//
// CANON FIDELITY (non-negotiable, Doc 23): the POR condition is set ONLY by a
// human via set_por_condition, rationale required, NEVER inferred. This
// detector produces candidate findings for the human who holds the right — it
// never sets any condition and never touches any authority state. A candidate
// is a question ("was this a deliberate hold?" / "what signal motivated this
// change?"), never a verdict: the disciplined hold is the patient half of
// Condition A, and an off-record reason may exist for any change. Detection
// proposes; authority disposes.
// ═════════════════════════════════════════════════════════════════════════════════

/** Policy window (days): a material signal older than this with no act after it is a B candidate; an act with no material signal within this window before it is a C candidate. */
export const CONDITION_WATCH_WINDOW_DAYS = 14;
/** Recency weighting decays linearly to zero over this horizon (matches the loaders' 60-day lookback). */
export const CONDITION_WATCH_RECENCY_HORIZON_DAYS = 60;

export const CONDITION_WATCH_PERSONA =
  'Condition Watch, a governance sentinel who notices when the rules for changing the plan are being bypassed — a signal nobody acted on, or a change nobody justified';

/** A signal on the record (agent finding, impact event, pulse trend, …). */
export interface GovSignal { kind: string; severity: CoreSeverity; occurredAt: string; ref: string; label: string; }
/** A governance act (rescoring, reprioritization trigger, catchball ball, list approval, …). */
export interface GovAct { kind: string; occurredAt: string; ref: string; label: string; }

const DAY_MS = 24 * 60 * 60 * 1000;

/** Signals material enough to demand a response — mirrors the loaders' filter. */
function isMaterial(s: GovSignal): boolean {
  return s.severity === 'critical' || s.severity === 'high';
}

// ── Detection ────────────────────────────────────────────────────────────────────

export function detectConditionCandidates(
  signals: GovSignal[],
  acts: GovAct[],
  opts: { windowDays?: number; nowIso: string },
): CoreDraftFinding[] {
  const findings: CoreDraftFinding[] = [];
  const windowDays = opts.windowDays ?? CONDITION_WATCH_WINDOW_DAYS;
  const now = Date.parse(opts.nowIso);
  if (!Number.isFinite(now)) return findings;

  const material = signals
    .map((s) => ({ s, ts: Date.parse(s.occurredAt) }))
    .filter((x) => isMaterial(x.s) && Number.isFinite(x.ts));
  const validActs = acts
    .map((a) => ({ a, ts: Date.parse(a.occurredAt) }))
    .filter((x) => Number.isFinite(x.ts));

  const daysBetween = (fromMs: number, toMs: number) => Math.floor((toMs - fromMs) / DAY_MS);
  /** 0..5, favouring recent events, linear decay over the recency horizon. */
  const recency = (ts: number) =>
    Math.max(0, 5 * (1 - (now - ts) / (CONDITION_WATCH_RECENCY_HORIZON_DAYS * DAY_MS)));
  const fmtDay = (iso: string) => iso.slice(0, 10);

  // ── Condition B candidates: a material signal past the window, no act after it ──
  for (const { s, ts } of material) {
    const daysSinceSignal = daysBetween(ts, now);
    if (daysSinceSignal < windowDays) continue; // still inside the policy window
    if (validActs.some((x) => x.ts > ts)) continue; // something governance did follow

    const severity: CoreSeverity = s.severity === 'critical' ? 'high' : 'medium';
    findings.push({
      base: {
        findingType: 'condition_b_candidate',
        severity,
        title: `A ${s.severity} signal has waited ${daysSinceSignal} days with no governance response`,
        itemIds: [],
        data: {
          candidate: 'condition_b',
          signal: { kind: s.kind, ref: s.ref, label: s.label, severity: s.severity, occurredAt: s.occurredAt },
          daysSinceSignal,
          windowDays,
          actsAfterSignal: 0,
        },
      },
      persona: CONDITION_WATCH_PERSONA,
      findingKind:
        'Condition B candidate (a material signal on record with no reprioritization act after it) — a question for the humans holding the right, never a verdict',
      fallbackSummary:
        `A ${s.severity} signal — ${s.label} (${fmtDay(s.occurredAt)}) — has been on the record for ` +
        `${daysSinceSignal} days with no reprioritization act after it (policy window: ${windowDays} days). ` +
        `Was this a deliberate hold?`,
      fallbackDetail:
        `This is a candidate for your review, not a verdict. Holding course is the correct response when the ` +
        `signal does not justify change — but that judgment belongs to the humans holding the reprioritization ` +
        `right, and right now nothing on the record says it was made. If the hold was deliberate, say so on the ` +
        `record; if the signal was missed, it is still waiting. Condition Watch never sets a condition: the ` +
        `Plan-of-Record condition is only ever set by a human via set_por_condition, with a rationale.`,
      weight: (s.severity === 'critical' ? 10 : 7) + recency(ts),
    });
  }

  // ── Condition C candidates: an act with no material signal in the window before it ──
  for (const { a, ts } of validActs) {
    const preceding = material.filter((x) => x.ts <= ts);
    if (preceding.some((x) => ts - x.ts <= windowDays * DAY_MS)) continue; // a signal backs it
    const nearest = preceding.sort((p, q) => q.ts - p.ts)[0] ?? null;

    findings.push({
      base: {
        findingType: 'condition_c_candidate',
        severity: 'medium',
        title: `A plan change on ${fmtDay(a.occurredAt)} has no signal on record behind it`,
        itemIds: [],
        data: {
          candidate: 'condition_c',
          act: { kind: a.kind, ref: a.ref, label: a.label, occurredAt: a.occurredAt },
          windowDays,
          nearestPrecedingSignal: nearest
            ? {
                label: nearest.s.label,
                occurredAt: nearest.s.occurredAt,
                gapDays: daysBetween(nearest.ts, ts),
              }
            : null,
        },
      },
      persona: CONDITION_WATCH_PERSONA,
      findingKind:
        'Condition C candidate (a reprioritization act with no material signal in the window before it) — a question for the humans holding the right, never a verdict',
      fallbackSummary:
        `${a.label} (${fmtDay(a.occurredAt)}) has no material signal on record in the ${windowDays} days before ` +
        `it${nearest ? ` — the nearest was ${daysBetween(nearest.ts, ts)} days earlier` : ' — none at all in the lookback'}. ` +
        `What signal motivated this change?`,
      fallbackDetail:
        `This is a candidate for your review, not a verdict. The reason may simply live off the record — a ` +
        `conversation, an instrument this system cannot see. But a plan change with no nameable signal is exactly ` +
        `the pattern that erodes trust in the list, so it is worth asking the question and, if a signal exists, ` +
        `putting it on the record. Condition Watch never sets a condition: the Plan-of-Record condition is only ` +
        `ever set by a human via set_por_condition, with a rationale.`,
      weight: 5 + recency(ts),
    });
  }

  return findings.sort((a, b) => b.weight - a.weight).slice(0, MAX_FINDINGS);
}
